/**
 * 任意テキスト / SSML の音声プレビューの副作用を担うフック。
 *
 * 分割前は `FreeTextFields` 内のローカル state (`previewing` / `previewError` / `previewUrl`)、
 * `previewAudioRef`、および再生速度反映の `useEffect` として実装されていたものを、
 * ロジックを変えずそのまま切り出したもの。
 *
 * 要件 2: プレビュー音声にも再生速度を反映するため、<audio> 要素に ref を張り、
 * playbackRate プロパティ変化時に手動で設定する。src が変わったときや
 * 再生開始時にリセットされてしまうことがあるため、呼び出し側の `onLoadedMetadata` でも
 * 再適用する (ref + onLoadedMetadata の両方で反映)。
 */
import { useEffect, useRef, useState } from 'react';

import type { PreviewSsmlHandler, PreviewSsmlResult } from '../types';

export interface UseAudioPreviewResult {
  /** プレビュー音声の生成中フラグ */
  previewing: boolean;
  /** プレビュー生成失敗時のエラーメッセージ (null = エラーなし) */
  previewError: string | null;
  /** 生成済みプレビュー音声の object URL (blob:)。null = 未生成 */
  previewUrl: string | null;
  /** プレビュー <audio> 要素への ref (playbackRate を手動設定するために使う) */
  previewAudioRef: React.RefObject<HTMLAudioElement | null>;
  /** 実効再生速度 (範囲 0.5〜2.0 外 / 未指定は 1.0 = 等倍) */
  effectivePlaybackRate: number;
  /**
   * 直近のプレビュー結果 (Issue #439)。
   *
   * サーバがレンダリングした文面 (`renderedSsml`)、採用された遅延パターン行
   * (`matchedPatternIndex`)、未展開プレースホルダ (`unresolvedTokens`) を含む。
   * `content` を指定せず従来形 (text / ssml) で呼んだ場合は `url` のみが入る。
   */
  lastResult: PreviewSsmlResult | null;
  /**
   * プレビュー音声を生成する (呼び出し側で入力内容とプレビュー可否を判定してから呼ぶ)。
   * 成功時は結果を、失敗時は `null` を返す (エラーは `previewError` に反映される)。
   */
  startPreview: (input: Parameters<PreviewSsmlHandler>[0]) => Promise<PreviewSsmlResult | null>;
}

export function useAudioPreview({
  onPreviewSsml,
  playbackRate,
}: {
  onPreviewSsml?: PreviewSsmlHandler;
  /**
   * 親コンテンツの `playback_rate` (要件 2)。
   * `undefined` / `1` は等倍再生。範囲 (0.5〜2.0) 外の値は 1.0 に落として無視する。
   */
  playbackRate?: number;
}): UseAudioPreviewResult {
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<PreviewSsmlResult | null>(null);

  const previewAudioRef = useRef<HTMLAudioElement | null>(null);

  /*
   * アンマウント時の object URL 解放 (Issue #439)。
   *
   * プレビュー部品 (`AudioPreviewField`) は各コンテンツ / 各遅延パターン行に
   * **複数同時に存在する** ようになったため、生成した blob URL を解放せずに
   * アンマウントすると、モーダルの開閉を繰り返すたびにメモリを消費し続ける。
   * `startPreview` 内の revoke (差し替え時) に加えて、アンマウント時にも解放する。
   *
   * `previewUrl` を deps に入れず ref で追跡するのは、「URL が変わるたびに
   * cleanup が走って再生中の音声が無効化される」のを避けるため
   * (差し替え時の解放は startPreview 側が責任を持つ)。
   *
   * ref への書き込みはレンダー中ではなく `useEffect` 内で行う。
   * レンダー中に ref を変更すると React の並行レンダリング下で
   * 破棄されたレンダー結果の値が残り得るため (`react-hooks/refs`)。
   */
  const previewUrlRef = useRef<string | null>(null);
  useEffect(() => {
    previewUrlRef.current = previewUrl;
  }, [previewUrl]);
  useEffect(
    () => () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    },
    [],
  );
  const effectivePlaybackRate =
    typeof playbackRate === 'number' && playbackRate >= 0.5 && playbackRate <= 2.0
      ? playbackRate
      : 1.0;
  useEffect(() => {
    if (previewAudioRef.current) {
      previewAudioRef.current.playbackRate = effectivePlaybackRate;
    }
  }, [effectivePlaybackRate, previewUrl]);

  const startPreview = async (
    input: Parameters<PreviewSsmlHandler>[0],
  ): Promise<PreviewSsmlResult | null> => {
    if (!onPreviewSsml) return null;
    setPreviewError(null);
    setPreviewing(true);
    try {
      // 直前のプレビュー URL は解放しておく
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      const result = await onPreviewSsml(input);
      setPreviewUrl(result.url);
      setLastResult(result);
      return result;
    } catch (err) {
      setPreviewError((err as Error).message || '音声プレビューの生成に失敗しました');
      // 生成に失敗したら前回の診断情報 (文面 / 未展開トークン) を残さない。
      // 古い `rendered_ssml` が新しいエラーの隣に並ぶと誤解を招くため。
      setLastResult(null);
      return null;
    } finally {
      setPreviewing(false);
    }
  };

  return {
    previewing,
    previewError,
    previewUrl,
    previewAudioRef,
    effectivePlaybackRate,
    lastResult,
    startPreview,
  };
}
